import React, { FC, useCallback } from "react";
import { FieldArray } from "react-final-form-arrays";
import { Field, useForm, useFormState } from "react-final-form";
import { makeStyles } from "@material-ui/core";
import UploadFile from "../../../../components/UploadFile/UploadFile";
import BootstrapInput from "../../../../components/BootstrapInput";
import Autocomplete from "../../../../components/Autocomplete";
import { mutateErrors } from "../../../../utils";
import useUserQueries from "../../../user/hooks/useUserQueries";
import { NotifyMode } from "../../../notify/NotifyModeEnum";
import { useNotify } from "../../../notify/useNotify";
import { IWizardStepContent, WizardMode } from "../../../wizard/types";
import { SECONDARY_SCHOOL_TYPE } from "../../constants";
import RequiredDocsMultiSelect from "../components/RequiredDocsMultiSelect";
import AddNewDocuments from "../components/AdditionalDocuments";
import { ViewAcademic } from "../components/ViewAcademic";
import SchoolGrade from "../components/SchoolGrade";
import { EducationType } from "../../../school/constants/types";

const useStyles = makeStyles(() => ({
  container: {
    width: "100%",
    marginTop: 16,
  },
  level: {
    marginBottom: 16,
  },
  subContainer: {
    "& + &": {
      marginTop: 16,
    },
  },
  schoolTypeName: {
    marginBottom: 16,
  },
}));

const ManageContent: FC<IWizardStepContent> = ({ handleSubmit }) => {
  const classes = useStyles();
  const { values } = useFormState();
  const {
    mutators: { push, remove },
  } = useForm();
  const { uploadApplication } = useUserQueries();
  const snackNotify = useNotify();

  const schoolLevels = values.injectedData.schoolLevels;
  const namePrefix = `schoolLevels.${values.selectedSchoolLevel.id}`;
  const selectedSchoolLevel = schoolLevels.find(
    (level) => level.id === values.selectedSchoolLevel.id,
  );
  const applicationForms =
    values.schoolLevels?.[values.selectedSchoolLevel.id]?.applicationForms;

  const grade =
    values.schoolLevels?.[values.selectedSchoolLevel.id]?.schoolGrade?.name || // todo correct this code line
    values.schoolLevels?.[values.selectedSchoolLevel.id]?.otherGrade;

  const selectedSchoolLevelAcademics =
    values.selectedSchoolLevel.id !== "_3"
      ? selectedSchoolLevel.academics[0]
      : selectedSchoolLevel.academics.find(
          (academic) => academic.grade === grade,
        );

  const addNewFiles = useCallback(
    async (files) => {
      const succeededFileNames: string[] = [];
      const errorMessages: string[] = [];

      for (const file of files) {
        try {
          let { data } = await uploadApplication(file);
          push(`${namePrefix}.applicationForms`, data);
          succeededFileNames.push(file.name);
        } catch (e) {
          const { message } = mutateErrors(e);
          errorMessages.push(`${file.name} - ${message}`);
        }
      }

      if (errorMessages.length > 0) {
        snackNotify("Error", errorMessages.join("\n"), NotifyMode.ERROR);
        return;
      }

      const successMessageEnding =
        succeededFileNames.length > 1
          ? "\nfiles have been uploaded."
          : " has been uploaded.";

      snackNotify(
        "Success",
        succeededFileNames.join("\n") + successMessageEnding,
        NotifyMode.SUCCESS,
      );
    },
    [namePrefix],
  );

  const removeFileHandler = useCallback(
    (file) => {
      const index = applicationForms.indexOf(file);

      remove(`${namePrefix}.applicationForms`, index);
    },
    [applicationForms, namePrefix, remove],
  );

  return (
    <form noValidate onSubmit={handleSubmit}>
      {values.mode === WizardMode.Edit ? (
        <ViewAcademic
          schoolLevelName={selectedSchoolLevel.name}
          schoolGrade={selectedSchoolLevelAcademics.grade}
        />
      ) : (
        <>
          <div className={classes.level}>
            <Field
              disableClearable={true}
              required
              fullWidth
              label="School Level"
              options={schoolLevels} //todo refactor
              placeholder="Please Select"
              // infoIcon={'World Education'}
              name={"selectedSchoolLevel"} //todo refactor
              component={Autocomplete}
            />
          </div>
          {selectedSchoolLevel.id === "_3" && (
            <div className={classes.level}>
              <SchoolGrade namePrefix={namePrefix} />
            </div>
          )}
        </>
      )}
      {schoolLevels.map((schoolLevel) => (
        <div key={schoolLevel.id}>
          {schoolLevel.id === values.selectedSchoolLevel.id &&
            (selectedSchoolLevelAcademics &&
            values.mode === WizardMode.Create ? (
              <ViewAcademic
                schoolTypeName={selectedSchoolLevelAcademics.schoolTypeName}
                requiredDocuments={selectedSchoolLevelAcademics.requiredDocs}
                applicationForms={selectedSchoolLevelAcademics.applicationForms}
                applicationLink={selectedSchoolLevelAcademics.applicationLink}
              />
            ) : (
              <div className={classes.container}>
                {values.mode === WizardMode.Edit ? (
                  <div className={classes.schoolTypeName}>
                    <ViewAcademic
                      schoolTypeName={
                        selectedSchoolLevelAcademics.schoolTypeName
                      }
                    />
                  </div>
                ) : (
                  <div className={classes.subContainer}>
                    <Field
                      disableClearable={true}
                      required
                      fullWidth
                      label="School Type"
                      options={SECONDARY_SCHOOL_TYPE}
                      placeholder="Please Select"
                      // infoIcon={'World Education'}
                      name={`${namePrefix}.schoolType`}
                      component={Autocomplete}
                    />
                  </div>
                )}
                <div className={classes.subContainer}>
                  <FieldArray
                    name={`${namePrefix}.requiredDocs`}
                    infoText={"Choose minimum 2 required documents"}
                    component={RequiredDocsMultiSelect}
                  />
                </div>
                <div className={classes.subContainer}>
                  <AddNewDocuments
                    namePrefix={namePrefix}
                    educationType={EducationType.Secondary}
                  />
                </div>
                <div className={classes.subContainer}>
                  <UploadFile
                    uploadedFiles={applicationForms}
                    addNewFiles={addNewFiles}
                    removeFile={removeFileHandler}
                    titleText={"Upload Application Form"}
                    infoText={
                      "You can browse hard copy of application form or/and any docs required."
                    }
                    allowedFileTypes={["jpg", "jpeg", "png", "pdf"]}
                    maxSize={30}
                    maxFiles={4}
                  />
                </div>
                <div className={classes.subContainer}>
                  <Field
                    fullWidth
                    type="text"
                    label="Application Link"
                    placeholder="www.edu.uk"
                    infoIcon={"You can put the link of application form"}
                    component={BootstrapInput}
                    name={`${namePrefix}.applicationLink`}
                  />
                </div>
              </div>
            ))}
        </div>
      ))}
    </form>
  );
};

export default ManageContent;
